/*
 * LegalMarketingTechCheck
 * A self-check that follows the post's 30-day rollout. The reader ticks what their firm already has in place.
 * Uses only the reader's answers. No scores, benchmarks or invented numbers.
 *
 * Rules
 *   Items are grouped in rollout order: Week 1 Measurement, Week 2 Intake, Week 3 Content and entities.
 *   The starting point is the FIRST group (in that order) with any item not ticked.
 *   The result names that week and lists its missing items, and links to the matching service page.
 *   Later groups with gaps are listed as "next" so nothing is hidden.
 *   If every item is ticked, the result points to Week 4 (Review) and the free marketing review.
 *   The result appears after the first tick or untick.
 * No tracking, storage or network calls.
 */
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";

type Item = { id: string; label: string };
type Group = { week: number; name: string; href: string; linkText: string; items: Item[] };

const GROUPS: Group[] = [
  {
    week: 1,
    name: "Measurement",
    href: "/services/law-firm-marketing-analytics",
    linkText: "See how we set up law firm marketing analytics →",
    items: [
      { id: "calls", label: "A separate call tracking number for each channel" },
      { id: "ga4", label: "GA4 key events for calls and form submissions" },
      { id: "crm", label: "Call tracking connected to your CRM" },
      { id: "cases", label: "Signed cases recorded against each lead's source" },
    ],
  },
  {
    week: 2,
    name: "Intake",
    href: "/services/lead-generation-for-law-firms",
    linkText: "See how we tighten intake and follow-up →",
    items: [
      { id: "textback", label: "Missed-call text-back" },
      { id: "schedule", label: "Online scheduling for consultations" },
      { id: "replytime", label: "A reply-time target for new inquiries" },
      { id: "followup", label: "Automated follow-up for unsigned leads" },
    ],
  },
  {
    week: 3,
    name: "Content and entities",
    href: "/services/ai-seo-geo",
    linkText: "See how we prepare firms for AI search →",
    items: [
      { id: "byline", label: "Attorney bylines and review dates on legal content" },
      { id: "schema", label: "Structured data on your key pages" },
      { id: "nap", label: "The same name, address and phone on your site, profile and directories" },
      { id: "practice", label: "Practice pages updated in the last year" },
    ],
  },
];

export default function LegalMarketingTechCheck() {
  const [have, setHave] = useState<Record<string, boolean>>({});
  const [touched, setTouched] = useState(false);

  const toggle = (id: string) => {
    setHave((s) => ({ ...s, [id]: !s[id] }));
    setTouched(true);
  };

  const gaps = GROUPS.map((g) => ({ group: g, missing: g.items.filter((i) => !have[i.id]) })).filter((x) => x.missing.length > 0);
  const start = gaps[0];
  const later = gaps.slice(1);

  const focusRing = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

  return (
    <section className="not-prose my-8 rounded-xl border border-border bg-card p-5 text-card-foreground sm:p-6">
      <h3 className="text-lg font-semibold">Where should your 30-day rollout start?</h3>
      <p className="mt-1 text-sm text-muted-foreground">Tick everything your firm already has working. Nothing is saved or sent anywhere.</p>

      <div className="mt-5 grid gap-5">
        {GROUPS.map((g) => (
          <fieldset key={g.week}>
            <legend className="text-sm font-semibold">
              Week {g.week}: {g.name}
            </legend>
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              {g.items.map((i) => {
                const on = !!have[i.id];
                return (
                  <Button
                    key={i.id}
                    type="button"
                    variant={on ? "default" : "outline"}
                    aria-pressed={on}
                    onClick={() => toggle(i.id)}
                    className={`h-auto whitespace-normal justify-start flex items-start gap-3 rounded-md border px-3 py-2 text-left text-sm ${focusRing} ${
                      on ? "border-primary bg-primary text-primary-foreground" : "border-input bg-card"
                    }`}
                  >
                    <span aria-hidden="true" className="mt-0.5 inline-block w-4 shrink-0 font-semibold">
                      {on ? "✓" : "○"}
                    </span>
                    <span>{i.label}</span>
                  </Button>
                );
              })}
            </div>
          </fieldset>
        ))}
      </div>

      <div aria-live="polite" className="mt-5 rounded-lg bg-muted p-4 text-sm">
        {!touched ? (
          <p className="text-muted-foreground">Tick what you have to see where to start.</p>
        ) : start ? (
          <div>
            <p className="font-semibold">
              Start with Week {start.group.week}: {start.group.name}.
            </p>
            <p className="mt-2">Still missing:</p>
            <ul className="mt-1 list-disc pl-5">
              {start.missing.map((i) => (
                <li key={i.id}>{i.label}</li>
              ))}
            </ul>
            {start.group.week === 1 && (
              <p className="mt-2 text-muted-foreground">
                Until each lead is tied to a signed case, you can't tell which other fixes are paying off.
              </p>
            )}
            {later.length > 0 && (
              <p className="mt-2 text-muted-foreground">
                Next: {later.map((x) => `Week ${x.group.week} (${x.missing.length} to add)`).join(", ")}.
              </p>
            )}
            <p className="mt-3">
              <Link to={start.group.href} className={`font-medium underline underline-offset-4 ${focusRing}`}>
                {start.group.linkText}
              </Link>
            </p>
          </div>
        ) : (
          <div>
            <p className="font-semibold">You're ready for Week 4: Review.</p>
            <p className="mt-2">
              Check cost per signed case by channel and move budget toward what signs cases.
            </p>
            <p className="mt-3">
              <Link to="/contact" className={`font-medium underline underline-offset-4 ${focusRing}`}>
                Request a free marketing review →
              </Link>
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
